/*
 * adapter.js — the swappable data-access seam. UI code depends ONLY on this
 * contract, never on Notion directly (brief: "distinct, swappable module").
 *
 * DataAdapter contract:
 *   resolveTenant(code)            → { id, name }                 (throws TENANT_NOT_FOUND)
 *   listTeamMembers(tenantId)      → [{ id, name, role, active }]
 *   resolveAssignment(employeeId)  → { project|null, confidence, assignmentId, ambiguous }
 *   listProjects(tenantId)         → [{ id, name, location }]
 *   submitCheckIn(session, iso)    → { success, recordId, checkedInAt, syncStatus, held }
 *   mode: 'live' | 'fixture'
 *   isDemo: boolean
 *
 * BUILD 001 additions (fixture-only):
 *   getAssignmentForDate(employeeId, dateISO)  → { project, assignmentId, workDate } | null
 *   listWorkforceForDate(tenantId, dateISO)    → [{ employeeName, assignedProjectName, ... }]
 *   saveAssignment({ employeeId, projectId, workDate })
 *   listEmployees(tenantId)
 */
import { createFixtureAdapter } from './fixtureAdapter.js'

/* ---------------------------------------------------------------------------
 * BUILD 001 FIXTURE LOCK
 *
 * Founder instruction: Build 001 must be INCAPABLE of reaching Business 001,
 * real Eagles Construction data, live payroll records, or the live Notion
 * adapter — not merely configured to prefer fixtures.
 *
 * Preferring fixtures was not enough. The previous default resolved the LIVE
 * adapter first and only fell back when a health probe failed, so a reachable
 * backend meant live data. This constant inverts that: while it is true, the
 * live adapter module is never imported, never constructed, and never called,
 * so no request to /api can originate from the client at all.
 *
 * TO RESTORE NORMAL BEHAVIOUR: set this to false. That is the whole change —
 * one line, fully reversible, and deliberately impossible to do by accident
 * through an env var or a missing .env file.
 * ------------------------------------------------------------------------- */
export const BUILD_001_FIXTURE_LOCK = true

const MODE = import.meta.env?.VITE_DATA_MODE // 'live' | 'fixture' | undefined(auto)

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms)
    promise.then((v) => { clearTimeout(t); resolve(v) },
                 (e) => { clearTimeout(t); reject(e) })
  })
}

/**
 * Resolve the active adapter once at startup.
 * @returns {Promise<{adapter:object, mode:string, isDemo:boolean, reason?:string}>}
 */
export async function resolveDataAdapter() {
  if (BUILD_001_FIXTURE_LOCK) {
    // Hard stop. No live module is loaded on this path.
    return wrap(createFixtureAdapter(), 'build-001-fixture-lock')
  }

  if (MODE === 'fixture') {
    return wrap(createFixtureAdapter(), 'forced-fixture')
  }

  // Dynamic import: the live adapter is only ever pulled in when the lock is
  // off, so locking the build also removes it from the executed code path.
  const { createNotionAdapter } = await import('./notionAdapter.js')
  const live = createNotionAdapter()

  if (MODE === 'live') {
    // Forced live — no silent fallback; surface failures honestly.
    return wrap(live, 'forced-live')
  }

  // Auto: prefer live, fall back to fixtures if the backend isn't answering.
  try {
    await withTimeout(live.health(), 2500)
    return wrap(live, 'backend-healthy')
  } catch (e) {
    return wrap(createFixtureAdapter(), `backend-unavailable: ${e.message}`)
  }
}

function wrap(adapter, reason) {
  return { adapter, mode: adapter.mode, isDemo: !!adapter.isDemo, reason }
}
